import { useEffect, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStyles } from '@material-ui/core'
import CheckpriceFormNew from './CheckpriceFormNew'
import T from './tokens'

// w/h are the resized intrinsic pixel dims (all ~×120 tall) — set as explicit
// attributes so the marquee reserves layout width and passes `unsized-images`.
const carriers = [
  { src: '/static/img/home-new/carriers/thaipost.png', alt: 'Thailand Post', w: 284, h: 120 },
  { src: '/static/img/home-new/carriers/kex.png', alt: 'Kerry Express KEX', w: 285, h: 120 },
  { src: '/static/img/home-new/carriers/dhl.png', alt: 'DHL', w: 284, h: 120 },
  { src: '/static/img/home-new/carriers/spx.png', alt: 'Shopee Express SPX', w: 284, h: 120 },
  { src: '/static/img/home-new/carriers/flash.png', alt: 'Flash Express', w: 284, h: 120 },
  { src: '/static/img/home-new/carriers/best.png', alt: 'BEST Express', w: 285, h: 120 },
  { src: '/static/img/home-new/carriers/jt.png', alt: 'J&T Express', w: 290, h: 120 },
  { src: '/static/img/home-new/carriers/ems.png', alt: 'EMS', w: 290, h: 120 },
  { src: '/static/img/home-new/carriers/ups.png', alt: 'UPS', w: 290, h: 120 },
  { src: '/static/img/home-new/carriers/sf.png', alt: 'SF Express', w: 290, h: 120 },
  { src: '/static/img/home-new/carriers/aramex.png', alt: 'Aramex', w: 290, h: 120 },
]

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    background: T.gradient.hero,
    overflow: 'hidden',
  },
  // First screen shows exactly the hero + the fixed-height carrier bar below:
  // hero = 100vh − carrierBar (100px), so the two together fill one viewport
  // and the next section's heading ("โปรโมชันและสิทธิพิเศษ") sits below the
  // fold. Definite height (not %-through-flex) so the banner's height:100%
  // resolves reliably.
  hero: {
    position: 'relative',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      height: 'calc(100vh - 100px)',
      overflow: 'hidden',
    },
  },
  bannerWrap: {
    position: 'relative',
    width: '100%',
    lineHeight: 0,
    [theme.breakpoints.up('md')]: {
      height: '100%',
    },
  },
  // Ambient AURORA layer — soft blurred brand-palette gradient blobs that
  // drift/morph across the hero (pure CSS, `screen` blend, behind the form at
  // zIndex 1; frozen under prefers-reduced-motion).
  auroraLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  auroraBlob: {
    position: 'absolute',
    borderRadius: '50%',
    willChange: 'transform',
    mixBlendMode: 'screen',
  },
  auroraBlob1: {
    width: '66%',
    paddingBottom: '66%',
    top: '-18%',
    left: '-12%',
    background:
      'radial-gradient(circle, rgba(0,102,204,0.75) 0%, rgba(0,102,204,0) 68%)',
    filter: 'blur(38px)',
    animation: '$auroraDrift1 2.625s ease-in-out infinite alternate',
  },
  auroraBlob2: {
    width: '58%',
    paddingBottom: '58%',
    top: '-12%',
    right: '-8%',
    background:
      'radial-gradient(circle, rgba(51,201,255,0.7) 0%, rgba(51,201,255,0) 68%)',
    filter: 'blur(34px)',
    animation: '$auroraDrift2 3.375s ease-in-out infinite alternate',
    animationDelay: '-6s',
  },
  auroraBlob3: {
    width: '54%',
    paddingBottom: '54%',
    bottom: '-20%',
    left: '4%',
    background:
      'radial-gradient(circle, rgba(0,158,255,0.65) 0%, rgba(0,158,255,0) 70%)',
    filter: 'blur(36px)',
    animation: '$auroraDrift3 2.4375s ease-in-out infinite alternate',
    animationDelay: '-10s',
  },
  auroraBlob4: {
    width: '40%',
    paddingBottom: '40%',
    bottom: '-16%',
    right: '8%',
    background:
      'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 68%)',
    filter: 'blur(34px)',
    animation: '$auroraDrift4 3s ease-in-out infinite alternate',
    animationDelay: '-3s',
  },
  // Cycles staggered per blob (animationDelay above) so they never pulse in unison.
  '@keyframes auroraDrift1': {
    '0%': { transform: 'translate(0, 0) scale(1)' },
    '50%': { transform: 'translate(14%, 16%) scale(1.22)' },
    '100%': { transform: 'translate(-10%, 6%) scale(0.88)' },
  },
  '@keyframes auroraDrift2': {
    '0%': { transform: 'translate(0, 0) scale(1)' },
    '50%': { transform: 'translate(-16%, 12%) scale(0.86)' },
    '100%': { transform: 'translate(8%, -12%) scale(1.2)' },
  },
  '@keyframes auroraDrift3': {
    '0%': { transform: 'translate(0, 0) scale(1)' },
    '50%': { transform: 'translate(12%, -14%) scale(1.18)' },
    '100%': { transform: 'translate(-14%, 5%) scale(0.9)' },
  },
  '@keyframes auroraDrift4': {
    '0%': { transform: 'translate(0, 0) scale(1)' },
    '50%': { transform: 'translate(-10%, -12%) scale(1.2)' },
    '100%': { transform: 'translate(10%, 10%) scale(0.85)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    auroraBlob1: { animation: 'none' },
    auroraBlob2: { animation: 'none' },
    auroraBlob3: { animation: 'none' },
    auroraBlob4: { animation: 'none' },
  },
  bannerImg: {
    display: 'block',
    width: '100%',
    height: 'auto',
    // Full banner scene shown edge-to-edge exactly as the design mockup
    // (world map + airplane + globe + ship + truck + boxes, card floating
    // over the centre). Natural 1672×941 aspect is kept until it would grow
    // taller than the viewport.
    // On wide screens the fixed aspect (height ≈ 56% of width) makes the
    // banner taller than the viewport, pushing the carrier logo bar below the
    // fold. Cap its height so the banner + logo bar fit one screen; cover-crop
    // (biased to the upper-centre where the plane + form card sit) keeps the
    // scene framed while trimming excess sky/floor.
    [theme.breakpoints.up('md')]: {
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center 35%',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 200,
      objectFit: 'cover',
      objectPosition: 'center bottom',
    },
  },
  // Form floats over the top-center of the banner on desktop,
  // and stacks just below the banner on mobile.
  formArea: {
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      // Pushed down from the design's original 13% so the banner's airplane
      // graphic (which sits roughly in the 11%-25% band of the banner
      // height) clears fully above the card, matching the mockup.
      top: '21%',
      left: '50%',
      transform: 'translateX(-50%)',
      // Narrower card so the ship (left) and truck (right) in the banner
      // behind it stay visible at the sides, per the design.
      width: '74%',
      maxWidth: 940,
      zIndex: 2,
    },
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      zIndex: 2,
      width: '92%',
      maxWidth: 620,
      margin: '-32px auto 0',
    },
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // "Logo Loop" (React Bits): the logo set is rendered twice in one flex
  // track; the JS-driven offset (rAF, see the component effect) wraps at
  // scrollWidth/2 so the identical second half seamlessly backs the first.
  carrierSection: {
    background: T.color.surface,
    padding: '22px 0',
    position: 'relative',
    zIndex: 1,
    overflow: 'hidden',
    // Drag-to-scroll affordance: grab cursor; `pan-y` passes vertical
    // page-scroll gestures through while horizontal ones drive the marquee;
    // `user-select:none` stops selection artefacts mid-drag.
    cursor: 'grab',
    touchAction: 'pan-y',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    // Desktop: fixed 100px height (matches the hero's `100vh − 100px`) so
    // hero + this bar sum to exactly one viewport — the bar lands at the
    // bottom of the first screen, logos always visible, promo heading below
    // the fold. border-box: the 22px top/bottom padding + 56px logo fit in
    // the 100px. No negative-margin overlap needed.
    [theme.breakpoints.up('md')]: {
      height: 100,
      boxSizing: 'border-box',
      zIndex: 3,
    },
    // Soft edge-fade masks (React Bits Logo Loop detail) — logos fade into
    // the bar's own white background at the far left/right instead of being
    // hard-clipped by `overflow:hidden`. Sit above the track (zIndex 2);
    // pointer-events none so hover/pause-on-hover on the bar underneath
    // still works through them.
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 96,
      zIndex: 2,
      pointerEvents: 'none',
      [theme.breakpoints.down('sm')]: {
        width: 48,
      },
    },
    '&::before': {
      left: 0,
      background: `linear-gradient(90deg, ${T.color.surface} 0%, rgba(255,255,255,0) 100%)`,
    },
    '&::after': {
      right: 0,
      background: `linear-gradient(270deg, ${T.color.surface} 0%, rgba(255,255,255,0) 100%)`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '14px 0',
    },
  },
  // Toggled onto `carrierSection` (classList) while a drag captures the
  // pointer. Declared after it so it wins the equal-specificity tie.
  carrierGrabbing: {
    cursor: 'grabbing',
  },
  carrierTrack: {
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
    flexWrap: 'nowrap',
    width: 'max-content',
    willChange: 'transform',
    // Motion is JS-driven now (was a pure-CSS `@keyframes` translateX loop) —
    // see the requestAnimationFrame + drag effect in the component below.
    [theme.breakpoints.down('sm')]: {
      gap: '16px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      // No auto-motion; dup hidden (carrierDup) so auto-margins center the row.
      margin: '0 auto',
    },
  },
  carrierLogo: {
    height: 56,
    width: 'auto',
    objectFit: 'contain',
    flexShrink: 0,
    transition: 'transform 0.3s ease',
    // Suppress the native image-drag ghost (paired with draggable={false})
    // so a mouse drag scrolls the strip instead of picking up the logo.
    WebkitUserDrag: 'none',
    userSelect: 'none',
    '&:hover': {
      transform: 'translateY(-3px)',
    },
    [theme.breakpoints.down('sm')]: {
      height: 42,
    },
  },
  // Duplicate logo set feeds the seamless loop (see comment above
  // `carrierSection`) at every breakpoint. Screen readers shouldn't
  // encounter the carrier names twice, so each duplicated <img> also carries
  // `aria-hidden` + empty alt in the JSX below; hidden outright under
  // reduced-motion, where only one (moving-free) copy should exist.
  carrierDup: {
    '@media (prefers-reduced-motion: reduce)': {
      display: 'none',
    },
  },
}))

// AURORA ambient effect — see the `auroraBlob*` styles above.
const HeroAurora = ({ className, blobClass, blob1, blob2, blob3, blob4 }) => (
  <div className={className} aria-hidden="true">
    <span className={`${blobClass} ${blob1}`} />
    <span className={`${blobClass} ${blob2}`} />
    <span className={`${blobClass} ${blob3}`} />
    <span className={`${blobClass} ${blob4}`} />
  </div>
)

const HeroNew = ({ required = false }) => {
  const classes = useStyles()
  const { t } = useTranslation('home')
  const sectionRef = useRef(null)
  const trackRef = useRef(null)

  // Carrier marquee: JS-driven transform loop with drag-to-scroll. A rAF loop
  // auto-advances an offset (~30s/set), wrapped into [0, scrollWidth/2) so
  // auto-scroll AND drag stay in the seamless loop. Paused on mouse-hover or
  // drag; a drag sets the offset from the pointer delta (1:1) and release
  // resumes from the CURRENT offset (no jump). Touch: pan-y + horizontal-
  // intent threshold keeps vertical page-scroll. Reduced-motion: no loop.
  useEffect(() => {
    const track = trackRef.current
    const section = sectionRef.current
    if (!track || !section) return undefined

    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const DURATION = 30000 // ms for one full carrier set (matches old 30s)
    const THRESHOLD = 6 // px of intent before a drag captures the pointer

    let raf = 0
    let last = 0
    let offset = 0
    let half = 0
    const drag = { down: false, active: false, decided: false, id: null, startX: 0, startY: 0, startOffset: 0 }
    let hover = false

    // One duplicated set = half the total flex-track width.
    const measure = () => { half = track.scrollWidth / 2 }
    const wrap = (v) => {
      if (half <= 0) return 0
      let n = v % half
      if (n < 0) n += half
      return n
    }
    const apply = () => {
      track.style.transform = `translate3d(${-offset}px, 0, 0)`
    }

    const tick = (now) => {
      if (!last) last = now
      const dt = now - last
      last = now
      if (!hover && !drag.active && half > 0) {
        offset = wrap(offset + (half / DURATION) * dt)
        apply()
      }
      raf = requestAnimationFrame(tick)
    }

    const endDrag = () => {
      drag.down = false
      if (drag.active) {
        drag.active = false
        section.classList.remove(classes.carrierGrabbing)
        try { section.releasePointerCapture(drag.id) } catch (e) { /* noop */ }
      }
      drag.id = null
      drag.decided = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', endDrag)
      window.removeEventListener('pointercancel', endDrag)
    }

    const onMove = (e) => {
      if (!drag.down) return
      const dx = e.clientX - drag.startX
      const dy = e.clientY - drag.startY
      if (!drag.decided) {
        if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return
        // Vertical intent → let the page scroll; abandon this gesture.
        if (Math.abs(dy) > Math.abs(dx)) {
          endDrag()
          return
        }
        drag.decided = true
        drag.active = true
        section.classList.add(classes.carrierGrabbing)
        try { section.setPointerCapture(drag.id) } catch (e) { /* noop */ }
      }
      if (drag.active) {
        if (e.cancelable) e.preventDefault()
        offset = wrap(drag.startOffset - dx)
        apply()
      }
    }

    const onDown = (e) => {
      if (media.matches) return
      if (e.pointerType === 'mouse' && e.button !== 0) return
      drag.down = true
      drag.decided = false
      drag.active = false
      drag.id = e.pointerId
      drag.startX = e.clientX
      drag.startY = e.clientY
      drag.startOffset = offset
      window.addEventListener('pointermove', onMove, { passive: false })
      window.addEventListener('pointerup', endDrag)
      window.addEventListener('pointercancel', endDrag)
    }

    // Hover-pause, mouse only (guard on pointerType so an emulated touch
    // pointer can't leave `hover` stuck true and freeze the loop).
    const onEnter = (e) => { if (e.pointerType === 'mouse') hover = true }
    const onLeave = (e) => { if (e.pointerType === 'mouse') hover = false }

    measure()
    const imgs = Array.prototype.slice.call(track.querySelectorAll('img'))
    // Logos load lazily; re-measure as each settles so `half` is accurate.
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener('load', measure)
    })
    window.addEventListener('resize', measure)
    section.addEventListener('pointerdown', onDown)
    section.addEventListener('pointerenter', onEnter)
    section.addEventListener('pointerleave', onLeave)

    if (!media.matches) raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
      imgs.forEach((img) => img.removeEventListener('load', measure))
      section.removeEventListener('pointerdown', onDown)
      section.removeEventListener('pointerenter', onEnter)
      section.removeEventListener('pointerleave', onLeave)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', endDrag)
      window.removeEventListener('pointercancel', endDrag)
    }
  }, [classes.carrierGrabbing])

  return (
    <section className={classes.root}>
      <h1 className={classes.srOnly}>{t('hero.srTitle')}</h1>
      <div className={classes.hero}>
        <div className={classes.bannerWrap}>
          {/* LCP element — eager + high priority (preloaded in pages/new).
              WebP with PNG fallback; width/height reserve the aspect box. */}
          <picture style={{ display: 'contents' }}>
            <source
              srcSet="/static/img/home-new/banner.webp"
              type="image/webp"
            />
            <img
              src="/static/img/home-new/banner.png"
              alt={t('hero.bannerAlt')}
              className={classes.bannerImg}
              width={1920}
              height={1024}
              fetchpriority="high"
              decoding="async"
            />
          </picture>
          <HeroAurora
            className={classes.auroraLayer}
            blobClass={classes.auroraBlob}
            blob1={classes.auroraBlob1}
            blob2={classes.auroraBlob2}
            blob3={classes.auroraBlob3}
            blob4={classes.auroraBlob4}
          />
        </div>
        <div className={classes.formArea}>
          <CheckpriceFormNew required={required} />
        </div>
      </div>

      <div className={classes.carrierSection} ref={sectionRef}>
        <div className={classes.carrierTrack} ref={trackRef}>
          {carriers.map((c) => (
            <img
              key={c.alt}
              src={c.src}
              alt={c.alt}
              className={classes.carrierLogo}
              width={c.w}
              height={c.h}
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          ))}
          {/* Duplicate set feeds the seamless marquee loop at every
              breakpoint (hidden under prefers-reduced-motion). Each image
              is aria-hidden with an empty alt so screen readers only ever
              encounter each carrier name once. */}
          {carriers.map((c) => (
            <img
              key={`dup-${c.alt}`}
              src={c.src}
              alt=""
              className={`${classes.carrierLogo} ${classes.carrierDup}`}
              width={c.w}
              height={c.h}
              loading="lazy"
              decoding="async"
              aria-hidden="true"
              draggable={false}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default HeroNew
